import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, FileText } from "lucide-react";
import { PageShell, ShellSection } from "./PageShell";
import { PRODUCTS, RESEARCH, RESOURCES, EXTERNAL_WORK, BUD, inquiryMailto } from "../data/content";
import { StatusBadge } from "../components/common";
import { Reveal } from "../components/Reveal";

export default function WarRoomPage() {
    const warroomProducts = PRODUCTS.filter((p) => p.division === "WarRoom Solutions");
    return (
        <PageShell
            testId="warroom-page"
            overline="WarRoom"
            metaTitle="WarRoom Solutions & WarRoom Labs — Titan Haven Group LLC"
            title="WarRoom Solutions & WarRoom Labs."
            lede="WarRoom Solutions builds commercial software and operating technology. WarRoom Labs develops emerging technology and applied research — and publishes THG's operational intelligence research."
        >
            <ShellSection>
                <Reveal>
                    <h2 className="font-display text-2xl md:text-3xl font-medium text-white">WarRoom Solutions — products</h2>
                </Reveal>
                <div className="mt-10 border-t border-thg-border">
                    {warroomProducts.map((p, i) => (
                        <Reveal key={p.slug} delay={i * 0.06} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start py-8 border-b border-thg-border">
                            <div className="md:col-span-4">
                                <h3 className="font-display text-xl font-medium text-white">{p.name}</h3>
                                {p.aka && <p className="mt-1 text-xs text-thg-tertiary">{p.aka}</p>}
                            </div>
                            <p className="md:col-span-5 text-sm leading-relaxed text-thg-secondary">{p.summary}</p>
                            <div className="md:col-span-3 flex md:justify-end items-center gap-4">
                                <StatusBadge>{p.status}</StatusBadge>
                                <Link
                                    to={`/portfolio/${p.slug}`}
                                    data-testid={`warroom-product-${p.slug}`}
                                    className="inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase text-thg-secondary hover:text-white transition-colors duration-300"
                                >
                                    View <ArrowRight size={13} />
                                </Link>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </ShellSection>
            <ShellSection>
                <Reveal>
                    <p className="text-xs tracking-[0.25em] uppercase font-bold text-thg-tertiary">The Discipline</p>
                    <h2 className="mt-4 font-display text-2xl md:text-3xl font-medium text-white">Operational Intelligence</h2>
                    <p className="mt-4 text-sm md:text-base leading-relaxed text-thg-secondary max-w-2xl">
                        Operational intelligence is the discipline connecting WarRoom research, operational briefs,
                        and the products THG builds — one operating method applied end to end: Observe &rarr; Question
                        &rarr; Synthesize &rarr; Act. The research examines how organizations lose sight of operational
                        reality; the briefs document that work; the products are built to keep that reality visible.
                    </p>
                </Reveal>
                <div className="mt-10 grid grid-cols-1 md:grid-cols-3 border-t border-l border-thg-border" data-testid="research-to-application">
                    {[
                        {
                            step: "Research",
                            text: "Operational intelligence research published through WarRoom Labs — execution visibility, KPI distortion, organizational drift.",
                            href: "https://warroomlabs.substack.com/",
                            linkLabel: "WarRoom Labs",
                        },
                        {
                            step: "Operating Insight",
                            text: "Our operating premise: performance measures drift from floor reality unless operating context is captured at the source.",
                        },
                        {
                            step: "Application",
                            text: "StoreCentral and Control Shift apply that discipline as commercial software for retail operations.",
                            href: "/portfolio/warroom-storecentral",
                            linkLabel: "See the products",
                            internal: true,
                        },
                    ].map((s, i) => (
                        <Reveal key={s.step} delay={i * 0.1} className="border-b border-r border-thg-border p-8 md:p-10">
                            <p className="text-xs tracking-[0.2em] font-bold text-thg-red uppercase">{s.step}</p>
                            <p className="mt-4 text-sm leading-relaxed text-thg-secondary">{s.text}</p>
                            {s.href && (
                                <a
                                    href={s.href}
                                    {...(s.internal ? {} : { target: "_blank", rel: "noopener noreferrer" })}
                                    className="mt-5 inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase text-thg-secondary hover:text-white transition-colors duration-300"
                                >
                                    {s.linkLabel} <ArrowUpRight size={13} />
                                </a>
                            )}
                        </Reveal>
                    ))}
                </div>
            </ShellSection>
            <ShellSection>
                <Reveal>
                    <h2 className="font-display text-2xl md:text-3xl font-medium text-white">WarRoom Labs — research</h2>
                    <p className="mt-4 text-sm md:text-base leading-relaxed text-thg-secondary max-w-2xl">
                        THG&rsquo;s emerging technology and applied research environment. Published research covers
                        Goodhart&rsquo;s Law in operating environments, KPI reinforcement loops, execution visibility,
                        organizational drift, customer-engagement degradation, and operational cognition.
                    </p>
                </Reveal>
                <Reveal delay={0.05} className="mt-8 bg-thg-surface border border-thg-border p-8 flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
                    <div className="md:flex-1">
                        <h3 className="font-display text-xl font-medium text-white">Bud&rsquo;s Market Intelligence Desk</h3>
                        <p className="mt-2 text-sm leading-relaxed text-thg-secondary">
                            An experimental WarRoom Labs market-intelligence desk, live at bud.titanhavengroup.com —
                            live market data, the Bud Basket v2.6 on a defined review cycle, and an archived call
                            record graded on the public record; members can follow the desk. Research and education
                            tooling, not investment advice.
                        </p>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                        <StatusBadge>{BUD.status}</StatusBadge>
                        <Link
                            to="/portfolio/bud"
                            data-testid="warroom-bud-link"
                            className="inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase text-thg-secondary hover:text-white transition-colors duration-300"
                        >
                            View <ArrowRight size={13} />
                        </Link>
                    </div>
                </Reveal>
                <div className="mt-10 border-t border-thg-border">
                    {RESEARCH.map((r, i) => (
                        <Reveal key={r.title} delay={i * 0.05} className="border-b border-thg-border">
                            <a
                                href={r.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`research-${i}`}
                                className="group flex items-center justify-between gap-6 py-5"
                                aria-label={`${r.title} — opens published research in a new tab`}
                            >
                                <div>
                                    <h3 className="font-display text-lg font-medium text-white group-hover:text-thg-secondary transition-colors duration-300">
                                        {r.title}
                                    </h3>
                                    <p className="mt-1 text-xs tracking-[0.15em] uppercase text-thg-tertiary">{r.topic}</p>
                                </div>
                                <ArrowUpRight size={16} className="shrink-0 text-thg-tertiary group-hover:text-white transition-colors duration-300" />
                            </a>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={0.1}>
                    <a
                        href="https://warroomlabs.substack.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="warroom-research-link"
                        className="mt-6 inline-flex items-center gap-2 text-sm text-thg-secondary hover:text-white transition-colors duration-300"
                    >
                        All WarRoom Labs research <ArrowUpRight size={15} />
                    </a>
                </Reveal>
            </ShellSection>
            <ShellSection>
                <Reveal>
                    <h2 className="font-display text-2xl md:text-3xl font-medium text-white">Resources</h2>
                    <p className="mt-4 text-sm md:text-base leading-relaxed text-thg-secondary max-w-2xl">
                        Published briefs, redacted case briefs, and practical tools — the Operational Intelligence
                        discipline in documented form.
                    </p>
                </Reveal>
                <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-thg-border border border-thg-border">
                    {RESOURCES.map((r, i) => (
                        <Reveal key={r.name} delay={i * 0.05} className="bg-[#0a0a0a]">
                            <a
                                href={r.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`resource-${i}`}
                                className="group flex items-start gap-4 p-8 hover:bg-thg-surface transition-colors duration-300 h-full"
                            >
                                <FileText size={18} className="shrink-0 mt-1 text-thg-red" />
                                <div>
                                    <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-thg-tertiary">{r.type}</p>
                                    <h3 className="mt-2 font-display text-lg font-medium text-white">{r.name}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-thg-secondary">{r.text}</p>
                                </div>
                            </a>
                        </Reveal>
                    ))}
                </div>
            </ShellSection>
            <ShellSection>
                <Reveal>
                    <h2 className="font-display text-2xl md:text-3xl font-medium text-white">Selected external work</h2>
                    <p className="mt-4 text-sm md:text-base leading-relaxed text-thg-secondary max-w-2xl">
                        Completed diagnostic and governance engagements for external clients. Client identities are
                        redacted or confidential by agreement.
                    </p>
                </Reveal>
                <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {EXTERNAL_WORK.map((w, i) => (
                        <Reveal key={w.title} delay={i * 0.06} className="bg-thg-surface border border-thg-border p-8 md:p-10">
                            <StatusBadge>{w.label}</StatusBadge>
                            <h3 className="mt-4 font-display text-xl font-medium text-white">{w.title}</h3>
                            <p className="mt-3 text-sm leading-relaxed text-thg-secondary">{w.text}</p>
                            <a
                                href={w.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`external-work-${i}`}
                                className="mt-5 inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase text-thg-secondary hover:text-white transition-colors duration-300"
                            >
                                View case brief <ArrowUpRight size={13} />
                            </a>
                        </Reveal>
                    ))}
                </div>
            </ShellSection>
            <ShellSection>
                <Reveal>
                    <p className="text-xs tracking-[0.25em] uppercase font-bold text-thg-tertiary">Engagements</p>
                    <h2 className="mt-4 font-display text-2xl md:text-3xl font-medium text-white">
                        Operational Intelligence &amp; Strategic Engagements
                    </h2>
                    <p className="mt-5 font-display text-lg md:text-xl text-white/90 max-w-2xl">
                        &ldquo;When reported performance and operating reality don&rsquo;t match, we find the gap.&rdquo;
                    </p>
                    <div className="mt-6 max-w-2xl space-y-4">
                        <p className="text-sm md:text-base leading-relaxed text-thg-secondary">
                            Titan Haven Group works directly with organizations on complex operating problems
                            involving execution, visibility, measurement, decision flow, management systems,
                            governance, and operating-model performance.
                        </p>
                        <p className="text-sm md:text-base leading-relaxed text-thg-secondary">
                            We conduct focused operational diagnostics, examine what the organization measures
                            against what is actually happening, identify where information or execution breaks
                            down, and develop practical actions around the problem.
                        </p>
                        <p className="text-xs leading-relaxed text-thg-tertiary" data-testid="warroom-engagement-rates">
                            Engagement ranges: operational and retail-performance diagnostics start at $7,500;
                            operating-system design, digital systems, and implementation engagements start at
                            $15,000; ongoing operational-intelligence and advisory support starts at $5,000 per
                            month, normally with a three-month minimum. Final fees depend on scope, access,
                            travel, data, procurement, and implementation needs.
                        </p>
                    </div>
                    <a
                        href={inquiryMailto("Operational Intelligence / Strategic Engagement Inquiry")}
                        data-testid="warroom-engagement-cta"
                        className="mt-8 inline-flex items-center gap-2 bg-thg-red text-white hover:bg-thg-redhover transition-colors duration-300 px-6 py-3 text-sm tracking-wide uppercase"
                    >
                        Put an Operating Problem in Front of Us <ArrowUpRight size={16} />
                    </a>
                </Reveal>
            </ShellSection>
        </PageShell>
    );
}
